/**
 * The + beside the phone's bottom bar, and the menu it opens.
 *
 * The web half of the app's + (`apps/mobile/src/shared/ui/bottom-nav/primary.tsx`,
 * owner 2026-10-18: "same menu as the app"). It sits OUTSIDE the capsule as its
 * own round button, which is what stops it reading as a fifth tab. Same entries,
 * same order as the app: MaJourneys AI, Plan your trip, Map, Transport.
 *
 * The shadow is on the button; the soft fill is clipped by the child inside it.
 * While the menu is open the + is pressed in like the active tab and turns to
 * a cross, so it says what the next tap does.
 */

import { useTranslation } from "react-i18next";
import { Car, Compass, Map, Plus, Sparkles, type LucideIcon } from "lucide-react";
import { floatPanelStyle, usePresence } from "../ui/use-presence";
import { SOFT_FILL, SOFT_SHADOW, SOFT_WELL } from "./soft-bar";

const NAVY = "#110E39";
const MUTED = "#5a5d77";

const ACTIONS: readonly { path: string; labelKey: string; Icon: LucideIcon }[] = [
  { path: "ai-chat", labelKey: "nav.ai_chat", Icon: Sparkles },
  { path: "trips", labelKey: "footer.plan_your_trip", Icon: Map },
  { path: "discover", labelKey: "nav.map", Icon: Compass },
  { path: "transport", labelKey: "nav.transport", Icon: Car },
];

type MenuHandle = {
  readonly open: boolean;
  readonly toggle: () => void;
  readonly ref: React.RefObject<HTMLDivElement | null>;
};

export function BottomBarPlus({
  menu,
  locale,
  onNavigate,
}: {
  readonly menu: MenuHandle;
  readonly locale: string;
  readonly onNavigate: (path: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const presence = usePresence(menu.open);

  return (
    <div ref={menu.ref} className="relative shrink-0">
      {presence.mounted ? (
        <div
          ref={presence.ref}
          role="menu"
          aria-label={t("nav.more")}
          data-state={presence.state}
          className={`mj-pop absolute end-0 bottom-[calc(100%+8px)] z-[var(--z-dropdown)] w-[232px] overflow-hidden py-1 ${
            presence.state === "closed" ? "pointer-events-none" : ""
          }`}
          style={{
            ...floatPanelStyle(22),
            // The menu grows out of the + it came from.
            transformOrigin: i18n.dir() === "rtl" ? "bottom left" : "bottom right",
          }}
        >
          {ACTIONS.map((a) => (
            <button
              key={a.path}
              type="button"
              role="menuitem"
              onClick={() => onNavigate(`/${locale}/${a.path}`)}
              className="flex w-full items-center gap-3 px-3.5 py-3 text-start text-[14.5px] font-semibold hover:bg-black/5"
              style={{ color: NAVY }}
            >
              <a.Icon size={17} strokeWidth={2} style={{ color: MUTED }} aria-hidden />
              <span className="min-w-0 flex-1">{t(a.labelKey)}</span>
            </button>
          ))}
        </div>
      ) : null}

      <button
        type="button"
        onClick={menu.toggle}
        aria-haspopup="menu"
        aria-expanded={menu.open}
        aria-label={menu.open ? t("common.close") : t("nav.more")}
        className="mj-press relative grid h-14 w-14 place-items-center rounded-full"
        style={{ boxShadow: SOFT_SHADOW, color: NAVY }}
      >
        <span
          aria-hidden
          className="absolute inset-0 overflow-hidden rounded-full"
          style={SOFT_FILL}
        >
          {menu.open ? <span className="absolute inset-[6px] rounded-full" style={SOFT_WELL} /> : null}
        </span>
        <Plus
          size={24}
          strokeWidth={2.4}
          aria-hidden
          className="relative transition-transform duration-200 ease-out motion-reduce:transition-none"
          style={{ transform: menu.open ? "rotate(45deg)" : undefined }}
        />
      </button>
    </div>
  );
}
